import clsx from 'clsx';
import React, {
  ChangeEvent, Suspense, useCallback, useEffect, useState
} from 'react';
import { AiFillCheckCircle } from 'react-icons/ai';
import { GoAlertFill } from 'react-icons/go';
import { MdCancel } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import {
  Tab, TabList, TabPanel,Tabs
} from 'react-tabs';
import { toast, ToastContainer } from 'react-toastify';
import 'react-tabs/style/react-tabs.css';
import 'react-toastify/dist/ReactToastify.css';

import { Button } from '@/components/button/button';
import { Loader } from '@/components/loader/loader';
import { Heading } from '@/components/typography/heading';
import { useCreateContract, useTaskStatus,useValidateContract } from '@/hooks/queries/useDataContracts';
import { ContractTaskStatus, ValidationState } from '@/types/dataContracts';

import FileUploadZone from './FileUploadZone';
import styles from './page.module.scss';
import ProcessingModal from './ProcessingModal';

// Backend raises ValueError on duplicate `contract_id` / URN collision; FastAPI
// wraps it as "Failed to create contract: Contract with contract_id=... already exists"
// or "...Contract ID collision...". Surface a friendly title + description instead.
const DUPLICATE_URN_PATTERN = /already exists|contract id collision|duplicate/i;

const friendlyCreateError = ( message: string ): React.ReactNode => {
  if ( DUPLICATE_URN_PATTERN.test( message ) ) {
    return (
      <div>
        <div className={ styles.toast_error_title }>Contract Already Exists</div>
        <div className={ styles.toast_error_description }>
          A contract with the same metadata was found. Duplicate contracts cannot be
          created. Please update the contract metadata and upload again.
        </div>
      </div>
    );
  }
  return message;
};

function CreateContractPage() {
  const navigate = useNavigate();

  const [ file, setFile ] = useState<File | null>( null );
  const [ yamlContent, setYamlContent ] = useState<string>( '' );
  const [ validatedYaml, setValidatedYaml ] = useState<string>( '' );
  const [ validationState, setValidationState ] = useState<ValidationState>( ValidationState.NONE );
  const [ validationMessage, setValidationMessage ] = useState<string>( '' );
  const [ showProcessingModal, setShowProcessingModal ] = useState( false );
  const [ taskId, setTaskId ] = useState<string | null>( null );
  const [ contractId, setContractId ] = useState<string | null>( null );

  const validateMutation = useValidateContract();
  const createMutation = useCreateContract();
  const { data: taskStatus, isLoading: isTaskLoading, error: taskError } = useTaskStatus(
    taskId,
    showProcessingModal && !!taskId
  );

  // Extract contract info from YAML using simple regex
  const extractField = useCallback( ( content: string, field: string ): string => {
    const regex = new RegExp( `^${ field }:\\s*(.+)$`, 'm' );
    const match = content.match( regex );
    return match ? match[1].trim().replace( /^["']|["']$/g, '' ) : '';
  }, [] );

  // Auto-validate on file upload
  const handleFileSelected = useCallback( async( selectedFile: File ) => {
    setFile( selectedFile );
    setValidationState( ValidationState.VALIDATING );
    setValidationMessage( '' );

    // Read file content
    const content = await selectedFile.text();
    setYamlContent( content );
    setValidatedYaml( content );

    try {
      const result = await validateMutation.mutateAsync( selectedFile );
      if ( result.is_valid ) {
        setValidationState( ValidationState.VALID );
        setValidationMessage( result.message || 'Contract is valid' );
      } else {
        setValidationState( ValidationState.INVALID );
        setValidationMessage( result.message || 'Contract validation failed' );
      }
    } catch( e ) {
      setValidationState( ValidationState.INVALID );
      setValidationMessage( e instanceof Error ? e.message : 'Validation error' );
    }
  }, [ validateMutation ] );

  // Detect YAML edits
  const handleYamlChange = ( e: ChangeEvent<HTMLTextAreaElement> ) => {
    const newContent = e.target.value;
    setYamlContent( newContent );

    if ( validationState === ValidationState.VALID || validationState === ValidationState.INVALID ) {
      if ( newContent !== validatedYaml ) {
        setValidationState( ValidationState.MODIFIED );
      } else {
        // Content reverted to validated version
        setValidationState( validateMutation.data?.is_valid ? ValidationState.VALID : ValidationState.INVALID );
      }
    }
  };

  // Re-validate after edit
  const handleValidate = async() => {
    if ( !yamlContent ) return;

    setValidationState( ValidationState.VALIDATING );
    setValidationMessage( '' );

    // Create a new File from the edited content
    const editedFile = new File( [ yamlContent ], file?.name || 'contract.yaml', { type: 'application/x-yaml' } );
    setFile( editedFile );

    try {
      const result = await validateMutation.mutateAsync( editedFile );
      setValidatedYaml( yamlContent );
      if ( result.is_valid ) {
        setValidationState( ValidationState.VALID );
        setValidationMessage( result.message || 'Contract is valid' );
      } else {
        setValidationState( ValidationState.INVALID );
        setValidationMessage( result.message || 'Contract validation failed' );
      }
    } catch( e ) {
      setValidationState( ValidationState.INVALID );
      setValidationMessage( e instanceof Error ? e.message : 'Validation error' );
    }
  };

  // Create contract
  const handleCreateContract = async() => {
    if ( !file || validationState !== ValidationState.VALID ) return;

    setShowProcessingModal( true );

    try {
      const result = await createMutation.mutateAsync( file );
      setTaskId( result.task_id );
      if ( result.contract_id ) setContractId( result.contract_id );
    } catch( e ) {
      const message = e instanceof Error ? e.message : 'Failed to create contract';
      toast.error( friendlyCreateError( message ) );
      setShowProcessingModal( false );
    }
  };

  // Navigate back to list — contract will appear with its current status
  const handleProcessingClose = () => {
    setShowProcessingModal( false );
    navigate( '/dataCatalog' );
  };

  // Navigate to details page for error info
  const handleViewDetails = () => {
    setShowProcessingModal( false );
    if ( contractId ) {
      navigate( '/dataCatalog/details', { state: { contractId } } );
    } else {
      navigate( '/dataCatalog' );
    }
  };

  // Toast on terminal success. Failures are surfaced inline by ProcessingModal,
  // so don't double up with a toast.
  useEffect( () => {
    if ( taskStatus?.status === ContractTaskStatus.SUCCESS ) {
      toast.success( 'Contract created successfully!' );
    }
  }, [ taskStatus?.status ] );

  const renderValidationStatus = () => {
    switch ( validationState ) {
      case ValidationState.VALIDATING:
        return (
          <div className={ clsx( styles.validation_status, styles.validation_validating ) }>
            <Loader /> Validating...
          </div>
        );
      case ValidationState.VALID:
        return (
          <div className={ clsx( styles.validation_status, styles.validation_valid ) }>
            <AiFillCheckCircle /> { validationMessage || 'Valid' }
          </div>
        );
      case ValidationState.INVALID:
        return (
          <div className={ clsx( styles.validation_status, styles.validation_invalid ) }>
            <MdCancel /> { validationMessage || 'Invalid' }
          </div>
        );
      case ValidationState.MODIFIED:
        return (
          <div className={ clsx( styles.validation_status, styles.validation_modified ) }>
            <GoAlertFill /> YAML modified - please re-validate
          </div>
        );
      default:
        return null;
    }
  };

  const renderContractInfo = () => {
    if ( !yamlContent ) {
      return <FileUploadZone onFileSelected={ handleFileSelected } />;
    }

    return (
      <div className={ styles.contract_info }>
        { file && (
          <div className={ styles.file_info }>
            File: { file.name }
          </div>
        ) }
        <div className={ styles.contract_info_row }>
          <label>Name</label>
          <span>{ extractField( yamlContent, 'name' ) || '-' }</span>
        </div>
        <div className={ styles.contract_info_row }>
          <label>Version</label>
          <span>{ extractField( yamlContent, 'version' ) || '-' }</span>
        </div>
        <div className={ styles.contract_info_row }>
          <label>Description</label>
          <span>{ extractField( yamlContent, 'description' ) || '-' }</span>
        </div>
        <div className={ styles.contract_info_row }>
          <label>Owner</label>
          <span>{ extractField( yamlContent, 'owner' ) || '-' }</span>
        </div>
        <div className={ styles.contract_info_row }>
          <label>Domain</label>
          <span>{ extractField( yamlContent, 'domain' ) || '-' }</span>
        </div>
      </div>
    );
  };

  return (
    <div className='page'>
      <ToastContainer />

      { showProcessingModal && (
        <ProcessingModal
          taskStatus={ taskStatus }
          isLoading={ isTaskLoading }
          error={ taskError }
          onClose={ handleProcessingClose }
          onViewDetails={ handleViewDetails }
        />
      ) }

      <div className={ clsx( 'layout__row', 'pad', 'layout__info' ) }>
        <span className="info_name">
          <Heading as="h2">Create New Contract</Heading>
        </span>
      </div>

      { validationState !== ValidationState.NONE && (
        <div className={ clsx( 'layout__row', 'pad' ) }>
          { renderValidationStatus() }
        </div>
      ) }

      <Tabs className='tabs'>
        <TabList>
          <Tab>Contract Information</Tab>
          <Tab disabled={ !yamlContent }>Edit Data Contract YAML</Tab>
        </TabList>

        <TabPanel className={ clsx( 'tabs', 'tab_panel' ) }>
          { renderContractInfo() }
        </TabPanel>

        <TabPanel className={ clsx( 'tabs', 'tab_panel' ) }>
          <textarea
            className={ styles.yaml_editor }
            value={ yamlContent }
            onChange={ handleYamlChange }
            spellCheck={ false }
          />
        </TabPanel>
      </Tabs>

      { yamlContent && (
        <div className={ styles.footer }>
          <Button
            variant='secondary'
            onClick={ () => navigate( '/dataCatalog' ) }
          >
            Cancel
          </Button>
          { ( validationState === ValidationState.MODIFIED || validationState === ValidationState.INVALID ) && (
            <Button
              variant='secondary'
              onClick={ handleValidate }
              loading={ validateMutation.isPending }
            >
              Validate
            </Button>
          ) }
          <Button
            variant='primary'
            onClick={ handleCreateContract }
            disabled={ validationState !== ValidationState.VALID }
            loading={ createMutation.isPending }
          >
            Create Contract
          </Button>
        </div>
      ) }
    </div>
  );
}

export default function Page() {
  return (
    <Suspense fallback={ <Loader /> }>
      <CreateContractPage />
    </Suspense>
  );
}
